import logger from '@eventbrite/client-logger';
import {
    client as braintreeClient,
    Client,
    paypalCheckout,
    PayPalCheckout,
} from 'braintree-web';

const PAYPAL_SDK_URL = 'https://www.paypal.com/sdk/js';

export enum PayPalFundingSource {
    credit = 'credit',
    paylater = 'paylater',
}

interface GlobalPayPalObject {
    isFundingEligible: (fundingSource: PayPalFundingSource) => boolean;
}

export interface WithGlobalPayPal {
    paypal?: GlobalPayPalObject;
}

let braintreeClientInstance: Client | null = null;
let paypalCheckoutInstance: PayPalCheckout | null = null;

const getOrCreateBraintreeClientInstanceIfNeeded = async (
    newBraintreeClientToken: PaypalScriptConfig['braintreeClientToken'],
): Promise<Client> => {
    if (braintreeClientInstance) {
        return braintreeClientInstance;
    }

    braintreeClientInstance = await braintreeClient.create({
        authorization: newBraintreeClientToken,
    });

    return braintreeClientInstance;
};

export const getPaypalCheckoutInstance = async () => {
    if (paypalCheckoutInstance) {
        return paypalCheckoutInstance;
    }

    if (!braintreeClientInstance) {
        throw new Error(
            'Attempted to create a PayPal Checkout Instance without a Braintree Client Instance',
        );
    }

    paypalCheckoutInstance = await paypalCheckout.create({
        client: braintreeClientInstance,
    });

    return paypalCheckoutInstance;
};

interface PaypalScriptConfig {
    amountCurrency: string;
    braintreeClientToken: string;
    disableFunding?: Array<PayPalFundingSource>;
    enableFunding?: Array<PayPalFundingSource>;
    locale?: string;
    onScriptLoad: () => void;
    onScriptLoadError: () => void;
    needsToReload?: boolean;
}

interface WithPayPalClientId {
    paypalClientId: string;
}

const getScriptSrc = ({
    amountCurrency,
    locale,
    paypalClientId,
    disableFunding = [],
    enableFunding = [],
}: Pick<
    PaypalScriptConfig,
    'amountCurrency' | 'locale' | 'enableFunding' | 'disableFunding'
> &
    WithPayPalClientId) => {
    const url = new URL(PAYPAL_SDK_URL);

    const searchParams = {
        'client-id': paypalClientId,
        currency: amountCurrency,
        locale,
        intent: 'authorize',
        components: ['buttons', 'messages', 'funding-eligibility'].join(','),
        'disable-funding': disableFunding.join(','),
        'enable-funding': enableFunding.join(','),
    };

    for (const [key, value] of Object.entries(searchParams)) {
        if (value) {
            url.searchParams.append(key, value);
        }
    }

    return url.toString();
};

const removePreviousScripts = ({
    amountCurrency,
    locale,
    paypalClientId,
}: Pick<PaypalScriptConfig, 'amountCurrency' | 'locale'> &
    WithPayPalClientId) => {
    const source = getScriptSrc({
        amountCurrency,
        locale,
        paypalClientId,
    });
    document
        .querySelectorAll(`script[src*="${source}"]`)
        .forEach((script) => script.remove());
};

const scriptExists = (source: string) =>
    document.querySelector(`script[src="${source}"]`) !== null;

const loadScript = ({
    amountCurrency,
    disableFunding,
    enableFunding,
    locale,
    onScriptLoad,
    onScriptLoadError,
    paypalClientId,
    needsToReload,
}: Pick<
    PaypalScriptConfig,
    | 'amountCurrency'
    | 'disableFunding'
    | 'enableFunding'
    | 'locale'
    | 'onScriptLoad'
    | 'onScriptLoadError'
    | 'needsToReload'
> &
    WithPayPalClientId) => {
    const source = getScriptSrc({
        amountCurrency,
        disableFunding,
        enableFunding,
        locale,
        paypalClientId,
    });

    if (needsToReload) {
        removePreviousScripts({
            amountCurrency,
            locale,
            paypalClientId,
        });
    } else if (scriptExists(source)) {
        onScriptLoad();
        return;
    }

    const node = document.createElement('script');

    node.src = source;
    node.onload = onScriptLoad;
    node.onerror = onScriptLoadError;

    document.head.appendChild(node);
};

export const loadPaypalScript = async ({
    amountCurrency,
    braintreeClientToken,
    disableFunding,
    enableFunding,
    locale,
    onScriptLoad,
    onScriptLoadError,
    needsToReload = false,
}: PaypalScriptConfig) => {
    try {
        const clientInstance = await getOrCreateBraintreeClientInstanceIfNeeded(
            braintreeClientToken,
        );

        const {
            gatewayConfiguration: {
                paypal: { clientId },
            },
        } = clientInstance.getConfiguration();

        loadScript({
            amountCurrency,
            disableFunding,
            enableFunding,
            locale,
            onScriptLoad,
            onScriptLoadError,
            paypalClientId: clientId,
            needsToReload,
        });
    } catch (error) {
        logger.error('Error loading PayPal SDK', {
            error,
            errorMessage: error.message,
        });
        onScriptLoadError();
    }
};
